import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Users, Activity, Target, RefreshCw, Calendar, BarChart3,
  Shield, AlertCircle, ArrowRight, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import RosterService, { SHIFTS, CARE_MINUTES_TARGET } from '../../services/rostering/RosterService';

const CareMinutesRealTimeDashboard = () => {
  const { state } = useAppContext();
  const { selectedFacility } = state;

  const [careMinutesData, setCareMinutesData] = useState(null);
  const [agencyReport, setAgencyReport] = useState(null);
  const [weeklyView, setWeeklyView] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);

      const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;

      const [cmResult, agencyResult, weekResult] = await Promise.all([
        RosterService.calculateCareMinutes(selectedFacility, selectedDate),
        RosterService.getAgencyReport(selectedFacility, monthStart, selectedDate),
        RosterService.getWeeklyView(selectedFacility, weekStart.toISOString().split('T')[0])
      ]);

      if (cmResult.success) setCareMinutesData(cmResult.data);
      if (agencyResult.success) setAgencyReport(agencyResult.data);
      if (weekResult.success) setWeeklyView(weekResult.data);
    } catch (error) {
      console.error('[CareMinutesDashboard] Load error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]" role="status">
        <div className="h-8 w-8 rounded-full border-2 border-indigo-200 border-t-indigo-600 animate-spin" />
        <p className="ml-3 text-gray-600">Loading care minutes data...</p>
      </div>
    );
  }

  if (!careMinutesData) {
    return (
      <div className="text-center py-12">
        <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No roster data available for this date.</p>
        <button onClick={loadData} className="mt-3 text-indigo-600 hover:text-indigo-800 font-medium text-sm">Refresh</button>
      </div>
    );
  }

  const { perResident, compliance, alertLevel, byShift, totalStaff, totalStaffHours, residentCount } = careMinutesData;

  const alertBg = alertLevel === 'green' ? 'from-emerald-600 to-green-600' : alertLevel === 'amber' ? 'from-amber-500 to-orange-500' : 'from-red-600 to-rose-600';
  const alertText = alertLevel === 'green' ? 'ON TARGET' : alertLevel === 'amber' ? 'AT RISK' : 'NON-COMPLIANT';

  return (
    <div className="space-y-6">
      {/* Header with Alert Level */}
      <div className={`bg-gradient-to-r ${alertBg} rounded-2xl p-6 text-white shadow-2xl`}>
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-8 h-8" />
              <h2 className="text-2xl font-bold">Real-Time Care Minutes</h2>
              <span className="bg-white bg-opacity-20 rounded-full px-3 py-1 text-sm font-bold">{alertText}</span>
            </div>
            <p className="opacity-90">Target: {CARE_MINUTES_TARGET.total} total, {CARE_MINUTES_TARGET.rn} RN per resident per day</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg px-3 py-2 text-white placeholder-white text-sm"
            />
            <button
              onClick={loadData}
              className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm">
            <div className="text-sm opacity-80">Total per Resident</div>
            <div className="text-3xl font-bold">{perResident.total}</div>
            <div className="text-xs opacity-70">target: {CARE_MINUTES_TARGET.total} min</div>
            <div className="mt-1">
              {compliance.totalCompliant
                ? <span className="text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Compliant</span>
                : <span className="text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Gap: {compliance.totalGap} min</span>
              }
            </div>
          </div>

          <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm">
            <div className="text-sm opacity-80">RN per Resident</div>
            <div className="text-3xl font-bold">{perResident.rn}</div>
            <div className="text-xs opacity-70">target: {CARE_MINUTES_TARGET.rn} min</div>
            <div className="mt-1">
              {compliance.rnCompliant
                ? <span className="text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Compliant</span>
                : <span className="text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Gap: {compliance.rnGap} min</span>
              }
            </div>
          </div>

          <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm">
            <div className="text-sm opacity-80">Total Staff</div>
            <div className="text-3xl font-bold">{totalStaff}</div>
            <div className="text-xs opacity-70">{totalStaffHours} hours total</div>
          </div>

          <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm">
            <div className="text-sm opacity-80">Residents</div>
            <div className="text-3xl font-bold">{residentCount}</div>
            <div className="text-xs opacity-70">in facility</div>
          </div>
        </div>
      </div>

      {/* Role Breakdown + Shift Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Minutes by Role */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Minutes per Resident by Role
          </h3>
          <div className="space-y-4">
            {[
              { label: 'Registered Nurse', value: perResident.rn, target: CARE_MINUTES_TARGET.rn, color: 'indigo' },
              { label: 'Enrolled Nurse', value: perResident.en, color: 'blue' },
              { label: 'Personal Care Asst', value: perResident.pca, color: 'green' },
              { label: 'Allied Health', value: perResident.allied, color: 'purple' }
            ].map(role => (
              <div key={role.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{role.label}</span>
                  <span className="font-bold">{role.value} min</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`bg-${role.color}-500 h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${Math.min(100, (role.value / (role.target || CARE_MINUTES_TARGET.total)) * 100)}%` }}
                  />
                </div>
                {role.target && (
                  <div className="text-xs text-gray-500 mt-0.5">Target: {role.target} min</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Shift Distribution */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            Shift Coverage
          </h3>
          <div className="space-y-4">
            {Object.entries(SHIFTS).map(([code, shift]) => {
              const shiftData = byShift[code] || { staff: 0, hours: 0, byRole: {} };
              return (
                <div key={code} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <span className="font-semibold text-gray-900">{shift.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{shift.start} — {shift.end}</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">{shiftData.staff} staff</span>
                  </div>
                  <div className="flex gap-2">
                    {['rn', 'en', 'pca', 'allied'].map(role => (
                      <span key={role} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        role === 'rn' ? 'bg-indigo-100 text-indigo-800' :
                        role === 'en' ? 'bg-blue-100 text-blue-800' :
                        role === 'pca' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {role}: {shiftData.byRole[role] || 0}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Agency Staff + Weekly Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agency Tracking */}
        {agencyReport && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-orange-600" />
              Agency Staff — NQIP Workforce Indicator
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <div className="text-sm text-green-700">Permanent Hours</div>
                  <div className="text-2xl font-bold text-green-900">{agencyReport.permanent.hours}</div>
                  <div className="text-xs text-green-600">{agencyReport.permanent.percentage}% — {agencyReport.permanent.uniqueStaff} staff</div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <div className="text-sm text-orange-700">Agency Hours</div>
                  <div className="text-2xl font-bold text-orange-900">{agencyReport.agency.hours}</div>
                  <div className="text-xs text-orange-600">{agencyReport.agency.percentage}% — {agencyReport.agency.uniqueStaff} staff</div>
                </div>
              </div>
              <div className={`flex items-center gap-2 p-3 rounded-lg ${agencyReport.nqipCompliant ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {agencyReport.nqipCompliant ? (
                  <><CheckCircle className="w-5 h-5 text-green-600" /><span className="text-sm font-medium text-green-700">NQIP Compliant — Agency usage below 25% threshold</span></>
                ) : (
                  <><AlertCircle className="w-5 h-5 text-red-600" /><span className="text-sm font-medium text-red-700">NQIP Non-Compliant — Agency usage {agencyReport.nqipIndicator}% exceeds 25% threshold</span></>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Weekly View */}
        {weeklyView && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Weekly Staffing Overview
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {weeklyView.map(day => (
                <div
                  key={day.date}
                  className={`rounded-xl p-3 border text-center cursor-pointer transition-all hover:shadow-md ${
                    day.date === selectedDate ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200' : 'bg-gray-50 border-gray-200'
                  }`}
                  onClick={() => setSelectedDate(day.date)}
                >
                  <div className="text-xs font-bold text-gray-500">{day.dayName}</div>
                  <div className="text-xs text-gray-400">{day.date.slice(5)}</div>
                  <div className="text-xl font-bold text-gray-900 my-1">{day.totalStaff}</div>
                  <div className="text-[9px] space-y-0.5">
                    <div className="text-blue-600">AM:{Array.isArray(day.byShift.AM) ? day.byShift.AM.length : day.byShift.AM}</div>
                    <div className="text-green-600">PM:{Array.isArray(day.byShift.PM) ? day.byShift.PM.length : day.byShift.PM}</div>
                    <div className="text-purple-600">N:{Array.isArray(day.byShift.NIGHT) ? day.byShift.NIGHT.length : day.byShift.NIGHT}</div>
                  </div>
                  {day.agencyCount > 0 && (
                    <div className="mt-1 text-[9px] text-orange-600 font-bold">{day.agencyCount} agency</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
          <span>{autoRefresh ? 'Auto-refreshing every 60s' : 'Auto-refresh paused'}</span>
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className="text-indigo-500 hover:text-indigo-700 font-medium"
        >
          {autoRefresh ? 'Pause' : 'Resume'} auto-refresh
        </button>
      </div>
    </div>
  );
};

export default CareMinutesRealTimeDashboard;
